// Child half of the embed protocol; the host half is Validate's `extractSdk.ts`. A message
// shape can't change on one side alone.
//
// Transport and routing only — the token lifecycle lives in `embedAuth.ts` and the theme in
// `embedTheme.ts`, both of which this file drives but neither of which depends on it.

import { TargetSaveAction } from '@/api/generated-types';
import { createHandoff } from '@/services/embed/embedHandoff';
import { setEmbedToken } from '@/services/embed/embedAuth';
import { applyTheme } from '@/services/embed/embedTheme';
import {
  chooseEmbedTarget,
  chooseNewValidation,
  setEmbedTarget,
  setEmbedTargetAction,
  takeTargetAbandoned,
  takeTargetRetargeted,
} from '@/stores/embedStore';
import { config } from '@/utils/config';
import { isEmbedded } from '@/utils/embed';

const ENVELOPE_SOURCE = 'hazcheck-extract' as const;
const PROTOCOL_VERSION = 1 as const;

/** The host names one document out of a merged validation; `extractCtuId` focuses a CTU within
 *  it. That is our own id for the CTU, the one a save stamps as its source, not the id Validate
 *  stores it under. */
export interface OpenTransaction {
  transactionId: string;
  extractCtuId?: string;
}

export interface BatchProgress {
  handled: number;
  total: number;
}

interface Envelope {
  source: typeof ENVELOPE_SOURCE;
  version: typeof PROTOCOL_VERSION;
  type: string;
  payload?: unknown;
}

let started = false;
let parentOrigin: string | null = null;
let appReadyPosted = false;
let appReadyFallbackTimer: ReturnType<typeof setTimeout> | null = null;

const APP_READY_FALLBACK_MS = 8000;

const filesHandoff = createHandoff<File[]>();
const openValidationHandoff = createHandoff<string>();
const openTransactionHandoff = createHandoff<OpenTransaction>();

/** Payload fields arrive as `unknown`; an empty string is as absent as a missing key. */
const str = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

const allowedParentOrigins = config.EMBED_PARENT_ORIGIN.split(',')
  .map((origin) => origin.trim().replace(/\/$/, ''))
  .filter(Boolean);

const readyTargetOrigins = (): string[] => {
  const allowed = allowedParentOrigins;
  try {
    if (document.referrer) {
      const referrerOrigin = new URL(document.referrer).origin;
      if (allowed.includes(referrerOrigin)) return [referrerOrigin];
    }
  } catch {
    // malformed referrer — fall through to the full allow-list
  }
  return allowed;
};

const isInboundEnvelope = (value: unknown): value is Envelope => {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (v.source !== ENVELOPE_SOURCE) return false;

  // A version bump on one side alone drops every message in silence, which looks like a dead
  // frame rather than a mismatch. Say so where someone can act on it.
  if (v.version !== PROTOCOL_VERSION) {
    if (import.meta.env.DEV) {
      console.warn(
        `[embed] dropped a v${String(v.version)} message; this build speaks v${PROTOCOL_VERSION}`
      );
    }
    return false;
  }

  return typeof v.type === 'string';
};

const postToParent = (type: string, payload?: unknown, origin?: string) => {
  const target = window.parent;
  if (!target || target === window) return;
  const envelope = { source: ENVELOPE_SOURCE, version: PROTOCOL_VERSION, type, payload };
  // Before a parent has spoken there is nothing to lock onto, and `auth-failed` can fire that
  // early — so an unlocked post goes to the whole allow-list rather than nowhere.
  const targets = origin ? [origin] : parentOrigin ? [parentOrigin] : readyTargetOrigins();
  targets.forEach((t) => target.postMessage(envelope, t));
};

// A reopen writes back into the validation it came from, so it is a target too: without one the
// save falls through to overwriting it whole, losing every other DGD in it.
const handleOpenValidation = (payload: { validationId?: unknown; origin?: unknown }) => {
  const validationId = str(payload.validationId);
  if (!validationId) return;

  // Anything but an explicit add-DGD is a reopen, so an older host that names no origin still
  // lands where it used to.
  const reopened = payload.origin !== 'add-dgd';

  setEmbedTarget({ validationId, reason: reopened ? 'reopen' : 'add-dgd' });
  if (reopened) openValidationHandoff.deliver(validationId);
};

// Retargets a running frame, where `open-validation` names the destination at launch.
// `validationId: null` clears it.
const handleSetTarget = (payload: { validationId?: unknown; action?: unknown }) => {
  if (payload.validationId === null) {
    if (!chooseNewValidation()) postDestinationLocked();
    return;
  }

  const validationId = str(payload.validationId);
  if (!validationId) return;

  const chosen = chooseEmbedTarget({
    validationId,
    // Picked rather than reopened, so it reads as adding to a validation the user chose.
    reason: 'add-dgd',
  });
  if (!chosen) {
    postDestinationLocked();
    return;
  }

  // Only an explicit replace replaces: anything else keeps the merge `chooseTarget` defaulted to,
  // so a malformed action costs the target nothing.
  if (payload.action === 'replace') setEmbedTargetAction(TargetSaveAction.Replace);
};

const handleMessage = (event: MessageEvent) => {
  if (!allowedParentOrigins.includes(event.origin)) return;
  if (event.source !== window.parent) return;
  if (!isInboundEnvelope(event.data)) return;

  // Lock onto the first parent that talks to us.
  if (!parentOrigin) parentOrigin = event.origin;
  else if (event.origin !== parentOrigin) return;

  const message = event.data;
  const payload = (message.payload ?? {}) as Record<string, unknown>;

  // No `default`: an unknown type is ignored on purpose, so the host can add messages ahead of a
  // build that understands them.
  switch (message.type) {
    case 'init': {
      const token = str(payload.token);
      if (token) setEmbedToken(token);
      if (payload.theme === 'light' || payload.theme === 'dark') applyTheme(payload.theme);
      return;
    }
    case 'token-refresh': {
      const token = str(payload.token);
      if (token) setEmbedToken(token);
      return;
    }
    case 'theme-change': {
      if (payload.theme === 'light' || payload.theme === 'dark') applyTheme(payload.theme);
      return;
    }
    case 'load-files': {
      const { files } = payload;
      if (Array.isArray(files) && files.length > 0 && files.every((f) => f instanceof File)) {
        filesHandoff.deliver(files as File[]);
      }
      return;
    }
    case 'open-validation':
      handleOpenValidation(payload);
      return;
    case 'set-target':
      handleSetTarget(payload);
      return;
    // Names the extraction outright, where `open-validation` can only name the validation and
    // guess between the extractions merged into it. The host sends one or the other, never both.
    case 'open-transaction': {
      const transactionId = str(payload.transactionId);
      if (transactionId) {
        openTransactionHandoff.deliver({
          transactionId,
          extractCtuId: str(payload.extractCtuId) ?? undefined,
        });
      }
      return;
    }
  }
};

export const startEmbedBridge = () => {
  if (started || !isEmbedded()) return;
  started = true;
  window.addEventListener('message', handleMessage);
  postToParent('ready', { version: PROTOCOL_VERSION, capabilities: { handlesAuth: true } });
};

export const onEmbedFiles = (handler: (files: File[]) => void): (() => void) =>
  filesHandoff.onDeliver(handler);

export const onOpenValidation = (handler: (validationId: string) => void): (() => void) =>
  openValidationHandoff.onDeliver(handler);

export const onOpenTransaction = (handler: (open: OpenTransaction) => void): (() => void) =>
  openTransactionHandoff.onDeliver(handler);

// `batch` is the counts as a boolean, for a host reading only it. A vanished target outranks a
// retarget: it changes what the host should say, where a retarget was intended.
export const postValidationCreated = (validationId: string, progress: BatchProgress) => {
  // The host drops a message with no id, so posting one would spend the abandonment on nothing.
  if (!validationId) return;

  const abandoned = takeTargetAbandoned();
  const retargeted = takeTargetRetargeted();
  const createdReason = abandoned ? 'target-missing' : retargeted ? 'retargeted' : null;
  const { handled, total } = progress;

  postToParent('validation-created', {
    validationId,
    batch: handled < total,
    handled,
    total,
    // Not the target's `reason`: this one says why the id differs from the one handed over.
    ...(createdReason && { reason: createdReason }),
  });
};

/** The host owns validation search; we only have fetch-by-id. Answered by `set-target`, or by
 *  nothing at all if the pick is cancelled. */
export const postRequestTargetPicker = () => postToParent('request-target-picker');

// Answered rather than dropped: silence would leave the host's picker looking successful.
const postDestinationLocked = () =>
  postError(
    'destination-locked',
    'This document is already saved to a validation and cannot be moved.'
  );

// The host holds its splash until this, hiding Extract's own boot waterfall.
export const postAppReady = () => {
  appReadyPosted = true;
  if (appReadyFallbackTimer) {
    clearTimeout(appReadyFallbackTimer);
    appReadyFallbackTimer = null;
  }
  postToParent('app-ready');
};

export const armAppReadyFallback = () => {
  if (!isEmbedded() || appReadyPosted || appReadyFallbackTimer) return;
  appReadyFallbackTimer = setTimeout(() => {
    appReadyFallbackTimer = null;
    if (import.meta.env.DEV) {
      console.warn(
        `[embed] no surface posted app-ready within ${APP_READY_FALLBACK_MS}ms; posting it centrally. The embedded surface should call postAppReady() on mount.`
      );
    }
    postAppReady();
  }, APP_READY_FALLBACK_MS);
};

// Nothing inside the embed can close the frame; the host owns it. This asks. Where to go next is
// the host's: it has the validation on its own route.
export const postExitRequested = (reason: 'saved' | 'cancelled') =>
  postToParent('exit-requested', { reason });

export const postError = (code: string, message: string) =>
  postToParent('error', { code, message });

export const postAuthFailed = () => postToParent('auth-failed');

// Success-only by design: a host that refreshes reflexively has no failure message to bounce off.
export const postAuthRecovered = () => postToParent('auth-recovered');
